import { Box, Button, Flex, Input, Portal, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { translate } from '../../../i18n';
import { SportType } from '../../../models/court/dto/court.dto';
import { typography } from '../../../theme/tokens';
import { GRID_DAY_ORDER, GridSlot, PricingGridState, formatSlotLabel, resolveCellPrice } from '../model';

type Props = {
    state: PricingGridState;
    slots: GridSlot[];
    sport: SportType;
    currencySymbol: string;
    blockColorFor: (blockId: string) => string;
    /** Apply `price` (or clear, when `null`) to `targetSlots` of `day`. */
    onApplyPrice: (day: number, targetSlots: GridSlot[], price: number | null) => void;
};

type Selection = { day: number; a: number; b: number };
/**
 * The price editor's anchor and viewport position. The grid lives in a
 * horizontal scroll container, and `overflow-x: auto` clips vertically too,
 * so an editor positioned inside it was cut off at the grid's bottom edge.
 * It is rendered in a portal at FIXED viewport coordinates instead, below
 * the last selected cell, or above it when there is no room below, and
 * re-anchored on every scroll or resize.
 */
type Editor = { day: number; from: number; to: number; left: number; top: number; placement: 'below' | 'above' };

const EDITOR_WIDTH = 220;
const EDITOR_HEIGHT = 200;
const EDITOR_GAP = 8;
const VIEWPORT_MARGIN = 8;

function editorPositionFor(cell: HTMLElement): Pick<Editor, 'left' | 'top' | 'placement'> {
    const r = cell.getBoundingClientRect();
    const roomBelow = window.innerHeight - r.bottom;
    const placement: Editor['placement'] = roomBelow >= EDITOR_HEIGHT + EDITOR_GAP || r.top < EDITOR_HEIGHT + EDITOR_GAP ? 'below' : 'above';
    const half = EDITOR_WIDTH / 2;
    const left = Math.min(Math.max(r.left + r.width / 2, half + VIEWPORT_MARGIN), window.innerWidth - half - VIEWPORT_MARGIN);
    const top = placement === 'below' ? r.bottom + EDITOR_GAP : r.top - EDITOR_GAP;
    return { left, top, placement };
}

const PRICE_PRESETS = [30, 40, 50, 60];

/**
 * The weekday × slot grid. Click a cell, or press and drag across several
 * cells of one day, and a small price editor opens under the selection.
 * Keyboard: a focused cell opens the editor on Enter/Space.
 */
export default function PricingGrid({ state, slots, sport, currencySymbol, blockColorFor, onApplyPrice }: Props) {
    const wrapRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [selection, setSelection] = useState<Selection | null>(null);
    const [dragging, setDragging] = useState(false);
    const [editor, setEditor] = useState<Editor | null>(null);
    const [draft, setDraft] = useState('');

    const maxPrice = useMemo(() => {
        let max = 0;
        for (const day of GRID_DAY_ORDER) {
            for (const slot of slots) {
                const price = resolveCellPrice(state, sport, day, slot);
                if (price !== null && price > max) max = price;
            }
        }
        return max;
    }, [state, slots, sport]);

    const border = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
    const headerText = useColorModeValue('secondaryGray.600', 'gray.400');
    const cellText = useColorModeValue('secondaryGray.900', 'white');
    const clearedText = useColorModeValue('gray.400', 'gray.600');
    const tier = useColorModeValue(['brand.50', 'brand.100', 'brand.200'], ['whiteAlpha.100', 'whiteAlpha.200', 'whiteAlpha.300']);
    const popoverBg = useColorModeValue('white', 'navy.800');
    const popoverBorder = useColorModeValue('secondaryGray.100', 'whiteAlpha.200');

    const cellBg = (price: number | null) => {
        if (price === null) return 'transparent';
        if (maxPrice <= 0) return tier[0];
        const ratio = price / maxPrice;
        return ratio < 0.5 ? tier[0] : ratio < 0.85 ? tier[1] : tier[2];
    };

    const openEditor = useCallback((sel: Selection) => {
        const from = Math.min(sel.a, sel.b);
        const to = Math.max(sel.a, sel.b);
        const wrap = wrapRef.current;
        const cell = wrap?.querySelector<HTMLElement>(`[data-cell="${sel.day}-${to}"]`);
        if (!wrap || !cell) return;
        const current = resolveCellPrice(state, sport, sel.day, slots[from]);
        setDraft(current === null ? '' : String(current));
        setEditor({ day: sel.day, from, to, ...editorPositionFor(cell) });
    }, [state, sport, slots]);

    // Keep the editor glued to its cell while anything scrolls or the window
    // resizes (the modal body, the grid's own horizontal scroll, the page).
    useEffect(() => {
        if (!editor) return;
        const reposition = () => {
            const cell = wrapRef.current?.querySelector<HTMLElement>(`[data-cell="${editor.day}-${editor.to}"]`);
            if (!cell) return;
            setEditor((current) => (current ? { ...current, ...editorPositionFor(cell) } : current));
        };
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [editor?.day, editor?.to]); // eslint-disable-line react-hooks/exhaustive-deps

    // Finish a drag anywhere on the page, not only over the table.
    useEffect(() => {
        if (!dragging) return;
        const onUp = () => {
            setDragging(false);
            if (selection) openEditor(selection);
        };
        window.addEventListener('mouseup', onUp);
        return () => window.removeEventListener('mouseup', onUp);
    }, [dragging, selection, openEditor]);

    useEffect(() => {
        if (editor && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editor]);

    const closeEditor = () => {
        setEditor(null);
        setSelection(null);
    };

    const commit = (raw: string | number | null) => {
        if (!editor) return;
        let price: number | null = null;
        if (raw !== null && raw !== '') {
            const parsed = Number(String(raw).replace(',', '.'));
            price = Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
            if (price === null) return; // keep the editor open on bad input
        }
        onApplyPrice(editor.day, slots.slice(editor.from, editor.to + 1), price);
        closeEditor();
    };

    const startDrag = (day: number, index: number, event: React.MouseEvent) => {
        event.preventDefault();
        setEditor(null);
        setSelection({ day, a: index, b: index });
        setDragging(true);
    };

    const extendDrag = (day: number, index: number) => {
        if (!dragging || !selection || selection.day !== day || selection.b === index) return;
        setSelection({ ...selection, b: index });
    };

    if (slots.length === 0) {
        return (
            <Text {...typography.body} color={headerText} textAlign="center" py={8}>
                {translate('courts.pricing_grid.no_slots')}
            </Text>
        );
    }

    const subtitle = editor
        ? editor.to > editor.from
            ? `${translate(`courts.pricing_grid.days_short.${editor.day}` as any)} · ${formatSlotLabel({ startMinute: slots[editor.from].startMinute, endMinute: slots[editor.to].endMinute })} · ${editor.to - editor.from + 1} ${translate('courts.pricing_grid.editor_slots')}`
            : `${translate(`courts.pricing_grid.days_short.${editor.day}` as any)} · ${formatSlotLabel(slots[editor.from])}`
        : '';

    return (
        <Box ref={wrapRef} position="relative" overflowX="auto" userSelect="none" data-testid="pricing-grid">
            <Table size="sm" style={{ tableLayout: 'fixed', minWidth: `${56 + slots.length * 92}px` }}>
                <Thead>
                    <Tr>
                        <Th w="56px" borderColor={border} />
                        {slots.map((slot, index) => (
                            <Th
                                key={index}
                                px={1}
                                textAlign="center"
                                whiteSpace="nowrap"
                                color={headerText}
                                borderBottomWidth="2px"
                                borderColor={blockColorFor(slot.blockId)}
                                {...typography.tableHeader}
                            >
                                {formatSlotLabel(slot)}
                            </Th>
                        ))}
                    </Tr>
                </Thead>
                <Tbody>
                    {GRID_DAY_ORDER.map((day) => {
                        const selRange = selection && selection.day === day ? [Math.min(selection.a, selection.b), Math.max(selection.a, selection.b)] : null;
                        return (
                            <Tr key={day}>
                                <Td px={2} fontWeight="600" fontSize="sm" color={cellText} borderColor={border}>
                                    {translate(`courts.pricing_grid.days_short.${day}` as any)}
                                </Td>
                                {slots.map((slot, index) => {
                                    const price = resolveCellPrice(state, sport, day, slot);
                                    const inSelection = selRange !== null && index >= selRange[0] && index <= selRange[1];
                                    const inEditor = editor !== null && editor.day === day && index >= editor.from && index <= editor.to;
                                    const highlighted = inSelection || inEditor;
                                    return (
                                        <Td
                                            key={index}
                                            data-cell={`${day}-${index}`}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`${translate(`courts.pricing_grid.days_short.${day}` as any)} ${formatSlotLabel(slot)}: ${price === null ? translate('courts.pricing_grid.unpriced_cell') : `${currencySymbol}${price}`}`}
                                            textAlign="center"
                                            px={0}
                                            py={3}
                                            fontSize="xs"
                                            fontWeight={price === null ? '400' : '600'}
                                            color={price === null ? clearedText : cellText}
                                            bg={cellBg(price)}
                                            cursor="pointer"
                                            borderWidth="1px"
                                            borderColor={border}
                                            borderTopColor={blockColorFor(slot.blockId)}
                                            boxShadow={highlighted ? 'inset 0 0 0 2px var(--chakra-colors-brand-500)' : undefined}
                                            transition="filter .12s"
                                            _hover={{ filter: 'brightness(.96)' }}
                                            onMouseDown={(e) => startDrag(day, index, e)}
                                            onMouseEnter={() => extendDrag(day, index)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    const sel = { day, a: index, b: index };
                                                    setSelection(sel);
                                                    openEditor(sel);
                                                }
                                            }}
                                        >
                                            {price === null ? '–' : `${currencySymbol}${price}`}
                                        </Td>
                                    );
                                })}
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>

            {editor && (
                <Portal>
                    <Box position="fixed" inset={0} zIndex={1500} onClick={closeEditor} />
                    <Box
                        position="fixed"
                        zIndex={1510}
                        left={`${editor.left}px`}
                        top={`${editor.top}px`}
                        w={`${EDITOR_WIDTH}px`}
                        transform={editor.placement === 'below' ? 'translateX(-50%)' : 'translate(-50%, -100%)'}
                    >
                        <Box
                            bg={popoverBg}
                            borderWidth="1px"
                            borderColor={popoverBorder}
                            borderRadius="16px"
                            boxShadow="0 20px 50px -18px rgba(15, 32, 72, .4)"
                            p={4}
                            w="100%"
                            data-testid="pricing-grid-editor"
                        >
                            <Text {...typography.formLabel} color={headerText} mb={1}>
                                {subtitle}
                            </Text>
                            <Flex align="center" gap={2} mb={3}>
                                <Text fontSize="2xl" fontWeight="700" color={cellText}>
                                    {currencySymbol}
                                </Text>
                                <Input
                                    ref={inputRef}
                                    variant="flushed"
                                    inputMode="decimal"
                                    fontSize="2xl"
                                    fontWeight="700"
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') commit(draft);
                                        if (e.key === 'Escape') closeEditor();
                                    }}
                                />
                            </Flex>
                            <Flex gap={2} mb={3}>
                                {PRICE_PRESETS.map((preset) => (
                                    <Button key={preset} size="xs" variant="lightBrand" flex={1} onClick={() => commit(preset)}>
                                        {currencySymbol}{preset}
                                    </Button>
                                ))}
                            </Flex>
                            <Flex gap={2}>
                                <Button size="sm" variant="ghost" colorScheme="red" onClick={() => commit(null)}>
                                    {translate('courts.pricing_grid.editor_clear')}
                                </Button>
                                <Button size="sm" variant="brand" flex={1} onClick={() => commit(draft)}>
                                    {translate('courts.pricing_grid.editor_save')}
                                </Button>
                            </Flex>
                        </Box>
                    </Box>
                </Portal>
            )}
        </Box>
    );
}
